/**
 * Acne detection via API Gateway → Lambda (YOLOv8-seg + Rekognition zone mapping).
 */

import type { CognitoUserSession } from 'amazon-cognito-identity-js';

export interface AcneDetection {
  x: number;
  y: number;
  w: number;
  h: number;
  confidence: number;
  class_name: string;
  mask_area: number;
  zone: string;
  accepted?: boolean;
  confidence_tier?: 'strong' | 'borderline';
}

/** Lambda-side quality gates (YOLO certainty vs borderline/low-signal). */
export interface AcneAnalysisQuality {
  tier?: string;
  mean_accepted_confidence?: number | null;
  borderline_detection_fraction?: number;
  yolo_uncertain?: boolean;
  confidence_min?: number;
  confidence_strong?: number;
}

export interface AcneProcessingStats {
  elapsed_ms?: number;
  onnx_inference_ms?: number;
  rekognition_ms?: number;
  accepted?: number;
  audit_rows_logged?: number;
  audit_accepted_logged?: number;
  audit_rejected_logged?: number;
  infer_pre_scale_ratio?: number;
  infer_resolution?: { width: number; height: number };
}

export type AcneAuditRow = {
  confidence?: number;
  class_name?: string;
  bbox_xyxy?: number[];
  zone?: string;
  accepted?: boolean;
  reject_reason?: string | null;
};

export interface AcneZoneCounts {
  forehead: number;
  nose: number;
  left_cheek: number;
  right_cheek: number;
  chin: number;
  jawline: number;
  other: number;
}

export interface AcneDetectionResult {
  success: boolean;
  total_detections: number;
  severity?: 'clear' | 'mild' | 'moderate' | 'severe' | 'very_severe';
  zone_counts: Partial<AcneZoneCounts> & Record<string, number>;
  detections: AcneDetection[];
  analysis_quality?: AcneAnalysisQuality;
  processing_stats?: AcneProcessingStats;
  audit?: AcneAuditRow[];
  face_detected: boolean;
  face_bounding_box?: {
    left: number;
    top: number;
    width: number;
    height: number;
  };
  face_landmarks?: Record<string, { x: number; y: number }>;
  age_range?: { Low: number; High: number };
  image_dimensions?: { width: number; height: number };
  message?: string;
}

const ACNE_DETECTION_API = (process.env.REACT_APP_ACNE_DETECTION_API || '').trim();

let __acneApiBannerLogged = false;
const logApiStatusOnce = () => {
  if (__acneApiBannerLogged) return;
  __acneApiBannerLogged = true;
  if (!ACNE_DETECTION_API) {
    console.warn(
      '[DEBUG] YOLOv8 acne detection DISABLED: REACT_APP_ACNE_DETECTION_API is not set in this build. Rebuild the frontend with the env var to enable.',
    );
  } else if (ACNE_DETECTION_API === 'disabled') {
    console.warn('[DEBUG] YOLOv8 acne detection DISABLED via REACT_APP_ACNE_DETECTION_API=disabled');
  } else {
    console.log('[DEBUG] YOLOv8 acne detection enabled →', ACNE_DETECTION_API);
  }
};

const blobToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      resolve(result.split(',')[1]);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

/**
 * Send a front-facing photo to the acne detection Lambda.
 * Returns null when REACT_APP_ACNE_DETECTION_API is unset or "disabled".
 */
export async function detectAcne(
  photo: File | Blob,
  session?: CognitoUserSession | null,
): Promise<AcneDetectionResult | null> {
  logApiStatusOnce();
  if (!ACNE_DETECTION_API || ACNE_DETECTION_API === 'disabled') {
    return null;
  }

  const base64Image = await blobToBase64(photo);

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (session) {
    const token = session.getIdToken().getJwtToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  }

  const t0 = performance.now();
  const response = await fetch(ACNE_DETECTION_API, {
    method: 'POST',
    headers,
    body: JSON.stringify({ image: base64Image }),
  });

  if (!response.ok) {
    const errorBody = await response.text();
    throw new Error(`Acne detection failed (${response.status}): ${errorBody}`);
  }

  const data: AcneDetectionResult = await response.json();
  console.log('[DEBUG] YOLOv8 Lambda response', {
    elapsedMs: Math.round(performance.now() - t0),
    httpStatus: response.status,
    success: data.success,
    face_detected: data.face_detected,
    total_detections: data.total_detections,
    severity: data.severity,
    yolo_uncertain: data.analysis_quality?.yolo_uncertain,
    lambda_elapsed_ms: data.processing_stats?.elapsed_ms,
    onnx_inference_ms: data.processing_stats?.onnx_inference_ms,
    rekognition_ms: data.processing_stats?.rekognition_ms,
    audit_accept_vs_reject: data.audit?.length
      ? {
          accepted: data.audit.filter((a) => a.accepted === true).length,
          rejected: data.audit.filter((a) => a.accepted === false).length,
        }
      : undefined,
  });

  if (!data.success) {
    throw new Error(data.message || 'Acne detection returned unsuccessful');
  }

  return data;
}

/**
 * Map Lambda zone_counts into per-region severity for the scan analysis pipeline.
 */
export const mapZoneCountsToRegions = (
  zoneCounts: AcneZoneCounts,
): Array<{
  region: string;
  activeLesionCount: number;
  severity: string;
}> => {
  const severityFromCount = (count: number): string => {
    if (count === 0) return 'none';
    if (count <= 2) return 'very_mild';
    if (count <= 5) return 'mild';
    if (count <= 8) return 'mild_to_moderate';
    if (count <= 12) return 'moderate';
    if (count <= 18) return 'moderate_to_severe';
    return 'severe';
  };

  const zones: Array<{ region: string; key: keyof AcneZoneCounts }> = [
    { region: 'forehead', key: 'forehead' },
    { region: 'nose', key: 'nose' },
    { region: 'left_cheek', key: 'left_cheek' },
    { region: 'right_cheek', key: 'right_cheek' },
    { region: 'chin', key: 'chin' },
    { region: 'jawline', key: 'jawline' },
  ];

  return zones.map(({ region, key }) => ({
    region,
    activeLesionCount: zoneCounts[key] || 0,
    severity: severityFromCount(zoneCounts[key] || 0),
  }));
};
